import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/ToastContainer";
import { campaignService } from "../services/campaignService";
import { parseApiError } from "../utils/errorHandler";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Send, Users, Loader2 } from "lucide-react";
import { StatusBadge } from "../components/email-campaigns/StatusBadge";
import { EmailPreview } from "../components/email-campaigns/EmailPreview";

export default function CampaignDetailsPage() {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  const [campaign, setCampaign] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const loadCampaign = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await campaignService.getCampaign(id);
      setCampaign(response.data);
    } catch (error) {
      console.error("Failed to load campaign:", error);
      const apiError = parseApiError(error);
      showError(
        apiError.message || "Failed to Load Campaign",
        "Failed to Load Campaign",
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, showError]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  const handleSend = async () => {
    if (
      !campaign ||
      (campaign.status !== "draft" && campaign.status !== "scheduled")
    ) {
      showError("Only draft or scheduled campaigns can be sent", "Cannot Send");
      return;
    }

    setIsSending(true);
    try {
      await campaignService.sendCampaign(id!);
      showSuccess("Campaign is being sent!", "Success");
      setIsSending(false);
      setTimeout(loadCampaign, 2000);
    } catch (error) {
      console.error("Failed to send campaign:", error);
      const apiError = parseApiError(error);
      showError(apiError.message || "Send Failed", "Send Failed");
      setIsSending(false);
    }
  };

  const handleCancel = async () => {
    if (!campaign || campaign.status !== "scheduled") {
      showError("Only scheduled campaigns can be cancelled", "Cannot Cancel");
      return;
    }

    setIsCancelling(true);
    try {
      await campaignService.cancelCampaign(id!);
      showSuccess("Campaign cancelled successfully", "Success");
      setIsCancelling(false);
      loadCampaign();
    } catch (error) {
      console.error("Failed to cancel campaign:", error);
      const apiError = parseApiError(error);
      showError(apiError.message || "Cancel Failed", "Cancel Failed");
      setIsCancelling(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center p-12">
          <Loader2 size={40} className="animate-spin text-[#e07828]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!campaign) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Button
              variant="outline"
              onClick={() => navigate("/email-campaigns")}
            >
              <ArrowLeft size={18} />
              Back to Campaigns
            </Button>
          </div>
          <div className="text-center py-12">
            <p className="text-[#989797]">Campaign not found</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/email-campaigns")}
            >
              <ArrowLeft size={18} />
              Back to Campaigns
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#102040]">
                {campaign.title}
              </h1>
              <StatusBadge status={campaign.status} />
            </div>
          </div>
          {(campaign.status === "draft" || campaign.status === "scheduled") && (
            <div className="flex gap-3">
              {campaign.status === "draft" && (
                <Button onClick={handleSend} disabled={isSending}>
                  {isSending ? (
                    <span className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send size={18} />
                      Send Now
                    </span>
                  )}
                </Button>
              )}
              {campaign.status === "scheduled" && (
                <Button
                  onClick={handleCancel}
                  disabled={isCancelling}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {isCancelling ? (
                    <span className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Cancelling...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Calendar size={18} />
                      Cancel Schedule
                    </span>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <Card className="h-full">
              <h2 className="text-lg font-bold text-[#102040] mb-4 pb-2 border-b border-gray-100">
                Campaign Info
              </h2>
              <div className="space-y-5">
                <div>
                  <p className="text-[10px] font-bold text-[#989797] uppercase tracking-wider mb-1">
                    Internal Title
                  </p>
                  <p className="text-sm font-semibold text-[#102040]">
                    {campaign.title}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[#989797] uppercase tracking-wider mb-1">
                    Email Subject
                  </p>
                  <p className="text-sm text-[#102040]">
                    {campaign.subject || "No subject set"}
                  </p>
                </div>
                {campaign.preheader && (
                  <div>
                    <p className="text-[10px] font-bold text-[#989797] uppercase tracking-wider mb-1">
                      Preheader
                    </p>
                    <p className="text-sm text-[#989797] italic">
                      {campaign.preheader}
                    </p>
                  </div>
                )}
                <div className="pt-2 border-t border-gray-50 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-bold text-[#989797] uppercase tracking-wider mb-1">
                      Recipients
                    </p>
                    <div className="flex items-center gap-1.5 text-sm font-medium text-[#102040]">
                      <Users size={14} className="text-[#e07828]" />
                      {campaign.targeting?.recipientsCount || 0}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-[#989797] uppercase tracking-wider mb-1">
                      Status
                    </p>
                    <StatusBadge status={campaign.status} />
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-50 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-[#989797] uppercase tracking-wider">
                      Created
                    </p>
                    <p className="text-[11px] text-[#102040]">
                      {formatDate(campaign.createdAt)}
                    </p>
                  </div>
                  {campaign.sentAt && (
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-bold text-[#989797] uppercase tracking-wider">
                        Sent
                      </p>
                      <p className="text-[11px] text-green-600 font-medium">
                        {formatDate(campaign.sentAt)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {campaign.description && (
              <Card>
                <h3 className="text-sm font-bold text-[#102040] mb-2">Notes</h3>
                <p className="text-xs text-[#989797] leading-relaxed">
                  {campaign.description}
                </p>
              </Card>
            )}
          </div>

          <div className="lg:col-span-2">
            <Card className="h-full p-0 overflow-hidden border-0 shadow-sm">
              <div className="bg-gray-50 border-b border-gray-100 px-4 py-3 flex items-center justify-between">
                <h2 className="text-sm font-bold text-[#102040]">
                  Email Content
                </h2>
                {campaign.templateId && (
                  <span className="text-[10px] font-medium px-2 py-0.5 bg-white border border-gray-200 rounded-full text-[#989797]">
                    Brevo Template #{campaign.templateId}
                  </span>
                )}
              </div>
              <div className="min-h-[600px] bg-white">
                <EmailPreview
                  templateId={campaign.templateId}
                  templateParams={campaign.templateParams}
                  subject={campaign.subject}
                  preheader={campaign.preheader}
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
